import { IAugmentationRepo } from "./irepo";
import {
  DataGenerationRequest,
  DataGenerationResponse,
  DataGenerationTask,
  SourceDatasetSize,
  SourceDatasetVariant,
  GeneratedDatasetListResponse,
  GeneratedDatasetDetail,
  HistogramRequest,
  HistogramResponse,
  DataGenerationEstimateRequest,
  PriceEstimateResponse,
  CostLogsResponse,
  CostDateFilterBy,
} from "./type/augmentation-def";
import { TError } from "../../types/error";

export class AugmentationUsecase {
  private repo: IAugmentationRepo;
  private error: (error: TError) => void;

  constructor(repo: IAugmentationRepo, error: (error: TError) => void) {
    this.repo = repo;
    this.error = error;
  }

  async estimatePrice(
    req: DataGenerationEstimateRequest
  ): Promise<PriceEstimateResponse> {
    try {
      return await this.repo.estimatePrice(req);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async getCostLogs(
    clientId: string,
    startDateUtc: string,
    endDateUtc: string,
    dateFilterBy?: CostDateFilterBy
  ): Promise<CostLogsResponse> {
    try {
      return await this.repo.getCostLogs(
        clientId,
        startDateUtc,
        endDateUtc,
        dateFilterBy
      );
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  // 当月の開始日（YYYY-MM-01）
  monthStartUtc(now: Date = new Date()): string {
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    return `${y}-${m}-01`;
  }

  // 当月の最終日（YYYY-MM-DD）
  monthEndUtc(now: Date = new Date()): string {
    const y = now.getFullYear();
    const m = now.getMonth();
    const last = new Date(y, m + 1, 0).getDate();
    return `${y}-${String(m + 1).padStart(2, "0")}-${String(last).padStart(2, "0")}`;
  }

  async getSourceSize(
    modelId: string,
    sourceDatasetVariant?: SourceDatasetVariant
  ): Promise<SourceDatasetSize> {
    try {
      return await this.repo.getSourceSize(modelId, sourceDatasetVariant);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async createTask(req: DataGenerationRequest): Promise<DataGenerationResponse> {
    try {
      return await this.repo.createTask(req);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async getTask(taskId: string): Promise<DataGenerationTask> {
    try {
      return await this.repo.getTask(taskId);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async listGeneratedDatasets(
    modelId: string,
    limit?: number,
    cursor?: string
  ): Promise<GeneratedDatasetListResponse> {
    try {
      return await this.repo.listGeneratedDatasets(modelId, limit, cursor);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async getGeneratedDataset(generatedDatasetId: string): Promise<GeneratedDatasetDetail> {
    try {
      return await this.repo.getGeneratedDataset(generatedDatasetId);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async getHistograms(
    generatedDatasetId: string,
    req: HistogramRequest
  ): Promise<HistogramResponse> {
    try {
      return await this.repo.getHistograms(generatedDatasetId, req);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async downloadDataset(generatedDatasetId: string): Promise<string> {
    try {
      return await this.repo.downloadDataset(generatedDatasetId);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  // 拡張後データポイント数 = サンプル数 × 列数（フロント計算）
  calcDataPoints(numSamples: number, columnCount: number): number {
    return numSamples * columnCount;
  }
}
